class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // Use event delegation on document so that even if the cart icon is re-rendered, it still works
    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;

      event.preventDefault();
      this.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;

      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    // Automatically ensure accessibility attributes are set on hover/focus/load
    const setAttrs = () => {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink) {
        if (cartLink.getAttribute('role') !== 'button') cartLink.setAttribute('role', 'button');
        if (cartLink.getAttribute('aria-haspopup') !== 'dialog') cartLink.setAttribute('aria-haspopup', 'dialog');
      }
    };

    document.addEventListener('focusin', setAttrs);
    document.addEventListener('mouseover', setAttrs, { passive: true });

    setAttrs();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // If the drawer opened and has only orphaned gift wrap or mismatch, trigger validation
    if (window.themeSettings && window.themeSettings.giftWrapProductId) {
      const giftWrapId = Number(window.themeSettings.giftWrapProductId);
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          let requiredGift = 0;
          let giftItem = null;
          (cart.items || []).forEach((it) => {
            if (it.product_id === giftWrapId) {
              giftItem = it;
            } else if (it.properties && (it.properties['Gift Wrap'] === 'Yes' || it.properties['Gift Wrap'] === 'yes')) {
              requiredGift += it.quantity;
            }
          });
          if (giftItem && requiredGift === 0) {
            // Clean up orphaned gift wrap item
            fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                id: giftItem.key,
                quantity: 0,
                sections: [this.dataset.sectionId || 'cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname,
              }),
            })
              .then((res) => res.json())
              .then((parsedState) => {
                this.renderContents(parsedState);
                const mainItems = (parsedState.items || []).filter((it) => it.product_id !== giftWrapId);
                if (mainItems.length === 0 || parsedState.item_count === 0) {
                  this.classList.add('is-empty');
                }
              })
              .catch((e) => console.error(e));
          }
        })
        .catch((e) => console.error(e));
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: this.dataset.sectionId || 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: this.dataset.sectionId || 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartProgressBar extends HTMLElement {
  connectedCallback() {
    const percent = parseFloat(this.dataset.percent) || 0;
    const fill = this.querySelector('.progress-bar-line-fill');
    if (!fill) return;

    // Retrieve previous percentage
    let prevPercent = window.cartPrevPercent;

    const drawer = this.closest('cart-drawer');
    const isDrawerActive = drawer && drawer.classList.contains('active');

    if (prevPercent === undefined && isDrawerActive) {
      prevPercent = 0;
    }

    if (prevPercent !== undefined) {
      // Set to previous first to allow transition to occur
      fill.style.width = `${prevPercent}%`;
      // Trigger transition to new percentage
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fill.style.width = `${percent}%`;
        });
      });

      // Check if any milestone was newly unlocked
      if (prevPercent < 25 && percent >= 25) {
        this.showToast('25');
      } else if (prevPercent < 50 && percent >= 50) {
        this.showToast('50');
      } else if (prevPercent < 75 && percent >= 75) {
        this.showToast('75');
      } else if (prevPercent < 100 && percent >= 100) {
        this.showToast('100');
      }
    } else {
      // First load: animate from 0 to current
      fill.style.width = '0%';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fill.style.width = `${percent}%`;
        });
      });
    }

    // Keep track of the current percentage
    window.cartPrevPercent = percent;
  }

  showToast(milestonePct) {
    const node = this.querySelector(`.progress-node[data-milestone="${milestonePct}"]`);
    if (!node || node.classList.contains('disabled-node')) return;
    const label = node.querySelector('.node-label-top').textContent;

    let drawerInner = this.closest('.drawer__inner');
    if (!drawerInner) return;

    // Remove existing toast if present
    const existingToast = drawerInner.querySelector('.cart-drawer__toast-notification');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = 'cart-drawer__toast-notification';
    toast.innerHTML = `<span>🎉 Unlocked: <strong>${label}</strong>!</span>`;
    drawerInner.appendChild(toast);

    // Slide down animation (delayed to sync with progress bar 600ms width transition)
    setTimeout(() => toast.classList.add('active'), 650);

    // Auto dismiss after 3 seconds (plus the 650ms entry delay)
    setTimeout(() => {
      toast.classList.remove('active');
      setTimeout(() => toast.remove(), 400);
    }, 3650);
  }
}

customElements.define('cart-progress-bar', CartProgressBar);